import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Form, ListGroup } from 'react-bootstrap';
import { ModulosService } from '../../../../service/modulos.service';
import { Modulo } from '../../../../service/types/cursos.types';
import PaginaManager from './PaginaManager';

interface ModuloManagerProps {
  cursoId: string;
}

export default function ModuloManager({ cursoId }: ModuloManagerProps) {
  const [modulos, setModulos] = useState<Modulo[]>([]);
  const [selectedModuloId, setSelectedModuloId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [titulo, setTitulo] = useState('');

  const load = async () => {
    const data = await ModulosService.findAllByCurso(cursoId);
    setModulos(data);
  };

  useEffect(() => {
    load();
    setSelectedModuloId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursoId]);

  const startCreate = () => {
    setTitulo('');
    setEditingId('new');
  };

  const startEdit = (modulo: Modulo) => {
    setTitulo(modulo.titulo);
    setEditingId(modulo.id);
  };

  const handleSave = async () => {
    if (!titulo.trim()) {
      alert('Ingrese un título para el módulo');
      return;
    }
    if (editingId === 'new') {
      await ModulosService.create(cursoId, { titulo });
    } else if (editingId) {
      await ModulosService.update(editingId, { titulo });
    }
    setEditingId(null);
    await load();
  };

  const handleDelete = async (modulo: Modulo) => {
    if (!window.confirm(`¿Eliminar el módulo "${modulo.titulo}"? Se eliminarán sus páginas.`)) return;
    await ModulosService.remove(modulo.id);
    if (selectedModuloId === modulo.id) setSelectedModuloId(null);
    await load();
  };

  const moveOrder = async (modulo: Modulo, direction: -1 | 1) => {
    const sorted = [...modulos].sort((a, b) => a.orden - b.orden);
    const idx = sorted.findIndex((m) => m.id === modulo.id);
    const swapWith = sorted[idx + direction];
    if (!swapWith) return;
    await Promise.all([
      ModulosService.update(modulo.id, { titulo: modulo.titulo, orden: swapWith.orden }),
      ModulosService.update(swapWith.id, { titulo: swapWith.titulo, orden: modulo.orden }),
    ]);
    await load();
  };

  const sorted = [...modulos].sort((a, b) => a.orden - b.orden);

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>Módulos del curso</span>
        <Button size="sm" variant="success" onClick={startCreate}>
          + Nuevo módulo
        </Button>
      </Card.Header>
      <Card.Body>
        {editingId === 'new' && (
          <div className="d-flex gap-2 mb-3">
            <Form.Control value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Título del módulo" />
            <Button variant="success" onClick={handleSave}>
              Guardar
            </Button>
            <Button variant="secondary" onClick={() => setEditingId(null)}>
              Cancelar
            </Button>
          </div>
        )}
        <ListGroup>
          {sorted.map((modulo, idx) => (
            <ListGroup.Item key={modulo.id}>
              {editingId === modulo.id ? (
                <div className="d-flex gap-2">
                  <Form.Control value={titulo} onChange={(e) => setTitulo(e.target.value)} />
                  <Button variant="success" onClick={handleSave}>
                    Guardar
                  </Button>
                  <Button variant="secondary" onClick={() => setEditingId(null)}>
                    Cancelar
                  </Button>
                </div>
              ) : (
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <Badge bg="secondary" className="me-2">
                      {modulo.orden}
                    </Badge>
                    <strong>{modulo.titulo}</strong>
                  </div>
                  <div className="d-flex gap-2">
                    <Button size="sm" variant="outline-secondary" disabled={idx === 0} onClick={() => moveOrder(modulo, -1)}>
                      ↑
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={idx === sorted.length - 1}
                      onClick={() => moveOrder(modulo, 1)}
                    >
                      ↓
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedModuloId === modulo.id ? 'info' : 'outline-info'}
                      onClick={() => setSelectedModuloId(selectedModuloId === modulo.id ? null : modulo.id)}
                    >
                      {selectedModuloId === modulo.id ? 'Ocultar páginas' : 'Administrar páginas'}
                    </Button>
                    <Button size="sm" variant="outline-primary" onClick={() => startEdit(modulo)}>
                      Editar
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => handleDelete(modulo)}>
                      Eliminar
                    </Button>
                  </div>
                </div>
              )}
              {selectedModuloId === modulo.id && (
                <div className="mt-3">
                  <PaginaManager moduloId={modulo.id} />
                </div>
              )}
            </ListGroup.Item>
          ))}
          {sorted.length === 0 && editingId !== 'new' && (
            <ListGroup.Item className="text-center text-muted">No hay módulos en este curso</ListGroup.Item>
          )}
        </ListGroup>
      </Card.Body>
    </Card>
  );
}
